import commonActions from '@/store/data/common/actions'
import { webRequestClient } from '@/services'
import { isEmpty, get, isNil, toNumber } from 'lodash'
import { errorHandler, getTokenUserEmail } from '@/utils/helpers'
import helpers from './helpers'
import Vue from 'vue'

const actions = {
    ...commonActions,
    retrieveItems({ commit, rootState }) {
        const _route = helpers.buildCclonesRoute(rootState.account)
        commit('ui/builder/setIsLoading', { item: ['cclone', 'list'], isLoading: true }, { root: true })
        return webRequestClient
            .get(_route.route, _route.params)
            .then(cclones => {
                commit('ui/builder/setIsLoading', { item: ['cclone', 'list'], isLoading: false }, { root: true })
                if (get(cclones, 'status') === 'KO') {
                    errorHandler('Error while retrieving cclones', commit)
                } else {
                    commit('setRetrievedItems', {
                        items: helpers.parseCclonesData(cclones.list)
                    })
                }
            })
            .catch(error => {
                commit('ui/builder/setIsLoading', { item: ['cclone', 'list'], isLoading: false }, { root: true })
                errorHandler(error, commit)
            })
    },

    retrieveCcloneDetails({ commit, rootState }, params) {
        const ccloneId = params.ccloneId
        const _route = helpers.buildCcloneDetailsRoute(rootState.account, ccloneId)
        commit('ui/builder/setIsLoading', { item: ['cclone', 'chart'], isLoading: true }, { root: true })
        return webRequestClient
            .get(_route.route, _route.params)
            .then(ccloneDetails => {
                commit('ui/builder/setIsLoading', { item: ['cclone', 'chart'], isLoading: false }, { root: true })
                if (get(ccloneDetails, 'status') === 'KO') {
                    errorHandler('There is no detail for cclone ' + ccloneId, commit)
                } else if (!isEmpty(ccloneDetails.list)) {
                    commit('setCcloneDetails', {
                        items: helpers.parseCcloneDetailsData(ccloneDetails.list[0].strong_classifiers_families_results),
                        ccloneId
                    })
                }
            })
            .catch(error => {
                commit('ui/builder/setIsLoading', { item: ['cclone', 'chart'], isLoading: false }, { root: true })
                errorHandler(error, commit)
            })
    },

    retrieveDecisionTree({ commit, rootState }, params) {
        return webRequestClient
            .get(`/wousdat/strong_classifiers_families/graph/${params.id}.json`, {
                params: { account_id: rootState.account.accountId, profile: params.profile, rank: params.rank }
            })
            .then(response => {
                if (response.status && response.status === 'KO') {
                    errorHandler('no decision tree found', commit)
                } else {
                    const bytes = new Uint8Array(response)
                    const binary = bytes.reduce((data, b) => (data += String.fromCharCode(b)), '')
                    commit('setDecisionTree', { status: 'success', image: btoa(binary) })
                }
            })
            .catch(error => {
                let errorMessage
                const apiError = get(error, 'apiError', null)
                if (isNil(apiError)) {
                    errorMessage = error
                } else {
                    if (error.apiError.status === 404) commit('setDecisionTree', { status: 'Decision tree not found', image: '' })
                    else {
                        errorMessage = isNil(get(error, 'apiError.message', null)) ? error.apiError.statusText : error.apiError.message
                        commit(
                            'notifications/setAddedNotification',
                            {
                                type: 'error',
                                message: errorMessage
                            },
                            { root: true }
                        )
                    }
                }
            })
    },

    createCclone({ commit, dispatch, rootState }, actionParams) {
        const _params = {
            ...actionParams.ccloneInfo,
            account_id: toNumber(rootState.account.accountId),
            country: rootState.account.country.code2,
            email: getTokenUserEmail(Vue.prototype.$keycloak)
        }
        return webRequestClient
            .post('/wousdat/strong_classifiers_families.json', _params)
            .then(cclone => {
                if (get(cclone, 'status', '') !== 'KO' && !isEmpty(cclone)) {
                    //retrieve all cclones + newly created cclone
                    dispatch('retrieveItems', {})
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Cclone ' + cclone.label + ' has been created'
                        },
                        { root: true }
                    )
                    dispatch('ui/cclone/closeCcloneCreationModal', {}, { root: true })
                } else {
                    const errorMessage = 'Error during cclone creation'
                    errorHandler(errorMessage, commit)
                }
            })
            .catch(error => errorHandler(error, commit))
    },

    deleteItem({ commit, dispatch }, params) {
        const route = `/wousdat/strong_classifiers_families/${params.itemId}.json`

        return webRequestClient
            .delete(route)
            .then(response => {
                if (response && !isEmpty(response)) {
                    dispatch('retrieveItems')
                    commit('ui/cclone/setSelectedRow', { rowId: null }, { root: true })
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: `CClone ${params.itemId} has been successfully deleted`
                        },
                        { root: true }
                    )
                } else {
                    errorHandler(`Error while deleting CCLone ${params.itemId}`, commit)
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },
    editCclone({ commit, dispatch, rootState }, params) {
        const path = `/wousdat/strong_classifiers_families/${params.ccloneId}.json`
        const _params = {
            ...params._ccloneInfo,
            status: 'waiting',
            account_id: toNumber(rootState.account.accountId),
            country: rootState.account.country.code2,
            email: getTokenUserEmail(Vue.prototype.$keycloak)
        }
        return webRequestClient
            .put(path, _params)
            .then(cclone => {
                const message = params.onlyStatus ? `Cclone ${cclone.label} status has been successfully updated` : `Cclone ${cclone.label} has been successfully updated`
                if (get(cclone, 'status', '') !== 'KO' && !isEmpty(cclone)) {
                    //replace cclone in store
                    commit('setUpdatedCclone', { item: cclone })
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message,
                        },
                        { root: true }
                    )
                } else {
                    const errorMessage =  params.onlyStatus ? 'Error during cclone status update' : 'Error during cclone update'
                    errorHandler(errorMessage, commit)
                }
            })
            .catch(error => errorHandler(error, commit))
            .finally(() => {
                dispatch('ui/cclone/closeCcloneCreationModal', {}, { root: true })
                commit('ui/cclone/setIsEditing', { isEditing: false }, { root: true })
            })
    },
}

export default actions
